/**
 * useCapabilityJobs - Fetches and filters jobs for a specific capability
 *
 * Uses adaptive polling: polls every 5 seconds when jobs are actively running,
 * stops polling when all jobs are complete or idle.
 *
 * Pattern follows useJobs.ts which uses useGenericSearch for job searches.
 * Using useGenericSearch instead of useMy because:
 * 1. useMy transforms queries incorrectly: `source:redis-login` becomes `#job#source:redis-login`
 * 2. Mutation polling only invalidates `genericSearch` queries, not `useMy` queries
 */

import { useMemo } from 'react';

import { Capability } from '@/hooks/useCapabilities';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { Job, JobStatus, ResourceType } from '@/types';
import { mergeJobStatus } from '@/utils/api';
import { getJobStatus } from '@/utils/job';

/** Polling interval when jobs are active (5 seconds) */
const CAPABILITY_JOBS_POLLING_INTERVAL = 5000;

export interface CapabilityJobsResult {
  jobs: Job[];
  status: JobStatus | undefined;
  isLoading: boolean;
  error: Error | null;
  hasRunningJobs: boolean;
  hasNoJobs: boolean;
  hasPendingJobs: boolean;
  refetch: () => void;
}

export function useCapabilityJobs(
  capability?: Capability
): CapabilityJobsResult {
  // Use useGenericSearch with source query to enable adaptive polling
  // This pattern matches useJobs.ts line 237-247 for source-based job searches
  // Query by source prefix since job.source = "capability-name#timestamp"
  const {
    data: jobsData,
    status: queryStatus,
    error,
    refetch,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: capability?.name ? `source:${capability.name}` : undefined,
    },
    {
      enabled: !!capability?.name,
      // Adaptive polling: only poll when there are active jobs
      refetchInterval: query => {
        const pages = query.state.data?.pages;
        if (!pages) return false;

        // Flatten all pages to get all jobs
        // useGenericSearch pages have structure: { data: { jobs: [...] }, offset, count }
        const jobs = pages.flatMap(page => page.data?.jobs || []) as Job[];

        const hasActiveJobs = jobs.some(job => {
          const status = getJobStatus(job);
          return (
            status === JobStatus.Running ||
            status === JobStatus.Queued ||
            status === JobStatus.AsyncRunning ||
            status === JobStatus.AsyncReturning
          );
        });

        // Poll at 5s when active jobs exist, stop when idle
        return hasActiveJobs ? CAPABILITY_JOBS_POLLING_INTERVAL : false;
      },
    }
  );

  const capabilityJobs = useMemo(() => {
    // Extract jobs from the processed useGenericSearch data
    const allJobs = jobsData?.jobs || [];

    if (!allJobs.length || !capability) return [];

    const filtered = allJobs.filter(job => {
      // Extract capability from source field (matches backend GetCapability pattern)
      // Backend format: "capability-name#timestamp"
      const jobCapability = job.source?.split('#')[0] || '';

      // Match by extracted capability name OR by key containing capability
      // Note: job.name is the executor IP address, NOT the capability name
      return (
        jobCapability === capability.name || job.key?.includes(capability.name)
      );
    });

    return filtered;
  }, [jobsData?.jobs, capability]);

  const aggregatedStatus = useMemo(() => {
    if (capabilityJobs.length === 0) return undefined;
    const statuses = capabilityJobs.map(
      job => job.status?.slice(0, 2) as JobStatus
    );
    return mergeJobStatus(statuses);
  }, [capabilityJobs]);

  /**
   * Sort jobs with active (running/queued) jobs first, then by updated timestamp descending.
   * This ensures the most relevant jobs appear at the top of the list.
   */
  const sortedJobs = useMemo(() => {
    if (!capabilityJobs || capabilityJobs.length === 0) return [];

    return [...capabilityJobs].sort((a, b) => {
      // Extract status codes (first 2 characters)
      const aStatus = a.status?.slice(0, 2) || '';
      const bStatus = b.status?.slice(0, 2) || '';

      // Active jobs (Running, Queued, AsyncRunning, AsyncReturning) come first
      const activeStatuses = [
        JobStatus.Running,
        JobStatus.Queued,
        JobStatus.AsyncRunning,
        JobStatus.AsyncReturning,
      ];
      const aIsActive = activeStatuses.includes(aStatus as JobStatus);
      const bIsActive = activeStatuses.includes(bStatus as JobStatus);

      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      // Then sort by updated timestamp descending (newest first)
      const aTime = a.updated || a.created || '';
      const bTime = b.updated || b.created || '';
      return bTime.localeCompare(aTime);
    });
  }, [capabilityJobs]);

  const hasRunningJobs = useMemo(() => {
    return capabilityJobs.some(job => {
      const status = getJobStatus(job);
      return status === JobStatus.Running || status === JobStatus.AsyncRunning;
    });
  }, [capabilityJobs]);

  const hasNoJobs = capabilityJobs.length === 0;

  const hasPendingJobs = useMemo(() => {
    return capabilityJobs.some(job => {
      const status = getJobStatus(job);
      return status === JobStatus.Queued || status === JobStatus.AsyncReturning;
    });
  }, [capabilityJobs]);

  return {
    jobs: sortedJobs,
    status: aggregatedStatus,
    isLoading: queryStatus === 'pending',
    error: error as Error | null,
    hasRunningJobs,
    hasNoJobs,
    hasPendingJobs,
    refetch,
  };
}
